"use client";

import React, { useState, useRef, useEffect } from 'react';

export default function ContactMain() {
  const sectionRef = useRef<HTMLElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el, i) => {
              setTimeout(() => el.classList.add('revealed'), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Mock submit — backend integration point
    setSubmitted(true);
  };

  return (
    <section ref={sectionRef} className="py-24" style={{ background: 'var(--color-background)' }}>
      <div className="max-w-8xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">

          {/* Left: Form — 3/5 */}
          <div className="lg:col-span-3 reveal-on-scroll">
            <div className="bg-white rounded-3xl border border-border p-8 lg:p-10 shadow-card">
              <div className="mb-8 space-y-2">
                <h2 className="font-display font-800 text-foreground" style={{ fontWeight: 800, fontSize: '28px' }}>發送查詢</h2>
                <p className="text-muted" style={{ fontSize: '15px' }}>請填寫以下表格，我們將於 1-2 個工作天內回覆。</p>
              </div>

              {submitted ? (
                <div className="flex flex-col items-center justify-center py-16 space-y-4 text-center">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: '#EEF7E6' }}>
                    <svg className="w-8 h-8 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="font-display font-700 text-foreground" style={{ fontWeight: 700, fontSize: '22px' }}>查詢已發送！</h3>
                  <p className="text-muted max-w-sm" style={{ fontSize: '15px' }}>感謝您的查詢，我們的團隊將盡快與您聯絡。如需緊急協助，請直接 WhatsApp 我們。</p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-4 px-6 py-3 rounded-xl font-display font-600 text-primary border border-primary hover:bg-primary-light transition-colors"
                    style={{ fontWeight: 600 }}
                  >
                    再次查詢
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-600 text-foreground mb-2" style={{ fontWeight: 600 }}>
                        姓名 Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="您的姓名"
                        className="form-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-600 text-foreground mb-2" style={{ fontWeight: 600 }}>
                        機構名稱 Company
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="醫院 / 診所名稱"
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-600 text-foreground mb-2" style={{ fontWeight: 600 }}>
                        電話 Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+852 XXXX XXXX"
                        className="form-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-600 text-foreground mb-2" style={{ fontWeight: 600 }}>
                        電郵 Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-600 text-foreground mb-2" style={{ fontWeight: 600 }}>
                      查詢類別 Subject
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="form-input"
                    >
                      <option value="">請選擇查詢類別</option>
                      <option value="life-support">生命資訊與支援設備</option>
                      <option value="ivd">體外診斷產品</option>
                      <option value="imaging">醫學影像設備</option>
                      <option value="maintenance">設備維修保養</option>
                      <option value="other">其他查詢</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-600 text-foreground mb-2" style={{ fontWeight: 600 }}>
                      查詢內容 Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="請詳細描述您的查詢或需求..."
                      className="form-input resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-display font-700 text-white transition-all duration-300 hover:opacity-90 hover:shadow-glow"
                    style={{ background: 'linear-gradient(135deg, #1E5F8E, #2472A4)', fontWeight: 700, fontSize: '15px' }}
                  >
                    發送查詢
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Right: Contact Info — 2/5 */}
          <div className="lg:col-span-2 space-y-6">
            {/* WhatsApp CTA */}
            <div className="reveal-on-scroll reveal-delay-1 rounded-2xl p-7 text-white" style={{ background: 'linear-gradient(135deg, #075E54, #128C7E)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <div>
                  <div className="font-display font-700 text-white" style={{ fontWeight: 700 }}>WhatsApp 即時查詢</div>
                  <div className="text-white/70 text-sm">Instant Message</div>
                </div>
              </div>
              <p className="text-white/80 text-sm leading-relaxed mb-5" style={{ fontSize: '14px' }}>
                即時透過 WhatsApp 聯絡我們，快速獲得專業醫療器材諮詢服務。
              </p>
              <a
                href="https://wa.me/85270347770"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full px-6 py-3.5 rounded-xl font-display font-700 text-white transition-all duration-300 hover:opacity-90"
                style={{ background: 'rgba(255,255,255,0.2)', border: '1.5px solid rgba(255,255,255,0.3)', fontWeight: 700, fontSize: '15px' }}
              >
                +852 7034 7770
              </a>
            </div>

            {/* Email */}
            <div className="reveal-on-scroll reveal-delay-2 bg-white rounded-2xl border border-border p-7 card-hover">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#EBF3FA' }}>
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <div className="font-display font-700 text-foreground" style={{ fontWeight: 700 }}>電郵查詢</div>
                  <div className="text-muted text-sm">Email Enquiry</div>
                </div>
              </div>
              <a
                href="mailto:lemonfrancie7@gmail.com"
                className="block text-primary font-600 hover:text-secondary transition-colors break-all"
                style={{ fontWeight: 600, fontSize: '15px' }}
              >
                lemonfrancie7@gmail.com
              </a>
              <p className="text-muted text-sm mt-2" style={{ fontSize: '13px' }}>我們將於 1-2 個工作天內回覆</p>
            </div>

            {/* Address */}
            <div className="reveal-on-scroll reveal-delay-3 bg-white rounded-2xl border border-border p-7 card-hover">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#EBF3FA' }}>
                  <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <div className="font-display font-700 text-foreground" style={{ fontWeight: 700 }}>公司地址</div>
                  <div className="text-muted text-sm">Office Address</div>
                </div>
              </div>
              <p className="text-foreground font-600 leading-relaxed" style={{ fontWeight: 600, fontSize: '15px' }}>
                荃灣區青山公路 483A 號
              </p>
              <p className="text-muted text-sm mt-1" style={{ fontSize: '13px' }}>
                483A Castle Peak Road, Tsuen Wan
              </p>
              <a
                href="https://maps.google.com/?q=483A+Castle+Peak+Road+Tsuen+Wan+Hong+Kong"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-3 text-primary text-sm font-600 hover:text-secondary transition-colors"
                style={{ fontWeight: 600 }}
              >
                在 Google Maps 查看
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>

            {/* Business hours */}
            <div className="reveal-on-scroll reveal-delay-4 bg-white rounded-2xl border border-border p-7">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#EEF7E6' }}>
                  <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <div className="font-display font-700 text-foreground" style={{ fontWeight: 700 }}>辦公時間</div>
                  <div className="text-muted text-sm">Business Hours</div>
                </div>
              </div>
              <div className="space-y-3">
                {[
                  { day: '星期一至五', hours: '09:00 – 18:00' },
                  { day: '星期六', hours: '09:00 – 13:00' },
                  { day: '星期日及公眾假期', hours: '休息' },
                ].map((item) => (
                  <div key={item.day} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                    <span className="text-muted text-sm" style={{ fontWeight: 500 }}>{item.day}</span>
                    <span
                      className={`text-sm font-600 ${item.hours === '休息' ? 'text-red-400' : 'text-foreground'}`}
                      style={{ fontWeight: 600 }}
                    >
                      {item.hours}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}